<template>
	<div style="display: flex; flex-direction: column; gap: 8px">
		<div v-if="!store.hasWeldingPairs">
			{{ $t("weld.weldMode.selectFacesPrompt") }}
		</div>
		<div v-else style="display: flex; flex-direction: column; gap: 8px">
			<div
				v-for="(pair, index) in store.weldingPairs"
				:key="index"
				style="border: 1px solid grey; border-radius: 4px; padding: 4px"
				@mouseenter="setWeldingPairHoverState(pair, true)"
				@mouseleave="setWeldingPairHoverState(pair, false)"
			>
				<div style="display: flex; align-items: center; justify-content: space-between">
					<div>
						{{ $t("weld.weldMode.weldlineLabel") }} {{ index + 1 }}:<br />
						<span v-for="(face, index2) in pair" :key="face.id">
							{{ getShortId(face.id) }} {{ index2 === 0 ? " x " : "" }}
							<span v-if="pair.length === 1 && !store.hasPairedWeldingFaces">
								{{ $t("weld.weldMode.selectFacePrompt") }}</span
							>
						</span>
					</div>
					<div>
						<button class="delete-button" @click="deletePair(index)">
							<svg-icon type="mdi" :path="mdiDelete"></svg-icon>
						</button>
					</div>
				</div>
			</div>
			<div v-if="store.hasPairedWeldingFaces">{{ $t("weld.weldMode.selectMoreFacesPrompt") }}</div>
			<div>
				<a
					href="https://blexon.com/hilfe/schweissnaht-definieren/"
					rel="noreferrer"
					target="_blank"
					>{{ $t("weld.weldMode.helpLink") }}</a
				>
			</div>
			<div v-if="store.hasWeldingPairs">
				<button
					class="blexon-button"
					:disabled="store.article.isReadOnly || !store.hasPairedWeldingFaces"
					@click="createWelds()"
					:title="$t('weld.weldMode.applyButtonTitle')"
				>
					{{ $t("weld.weldMode.applyButton") }}
				</button>
			</div>
		</div>
		<Weldlines></Weldlines>
	</div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useMainStore } from "@/store/mainStore";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiDelete } from "@mdi/js";
import Weldlines from "./Weldlines.vue";
import { LoadingState } from "@/model/Enums";
import { BlexonErpApi } from "@/store/BlexonErpApi";
import { IWeldlineDto } from "@/model/IWeldlineDto";
import { IFace } from "@/model/IFace";
import * as THREE from "three";

const store = useMainStore();
const { t } = useI18n();

const getShortId = (id: string): string => {
	const parts = id.split(":").filter((id) => id.trim() !== "");
	return parts.length > 0 ? parts.at(-1)! : "n/a";
};

const deletePair = (pairIndex: number) => {
	const weldingPair = store.weldingPairs[pairIndex];
	if (weldingPair.length === 2) {
		store.cam.setFaceColorById(weldingPair[0].groupId, null);
		store.cam.setFaceColorById(weldingPair[1].groupId, null);
	}
	store.weldingPairs.splice(pairIndex, 1);
};
const createWelds = async () => {
	try {
		console.log("Creating the defined welds...");
		store.setLoadingState(LoadingState.SettingFeature);
		const originalExportIds = store.weldingPairs.flatMap((wp) =>
			wp.map((face) => face.originalId)
		);
		const { newWeldlines, errorMessages } = await BlexonErpApi.defineWeldlines(
			store.article.id,
			originalExportIds,
			store.weldlines.map((w) => w.weldlineId!),
			store.skipValidation
		);
		// If the task fully failed, an exception was already thrown in BlexonErpApi

		// If there are no errors, the temporary weldlines are removed.
		// There are situations where a weldline can't be created but no error message is created.
		// In this case the temporary weldlines should still be removed.
		if (!errorMessages.length) {
			store.emptyAllWeldingPairs();
		}

		// If there are any defined weldlines, add them to the local list.
		if (newWeldlines.length > 0) {
			store.emptyAllWeldingPairs();
			newWeldlines.forEach((wl: IWeldlineDto) => store.weldlines.push(wl));
			store.isFileSaved = false;
		}

		// If there are any error Messages, then not all the weldlines could be set.
		// The ones which were successful were added to the list.
		// Show all the errors in a dialog. This is not perfect because we don't have good identifiers
		// for the faces but it's better than nothing and shows, that not everything was successful.
		if (errorMessages.length > 0) {
			let text = "";
			if (store.validationParameter && store.validationParameter.maximalDistanceForWeldline) {
				text = `${store.validationParameter.maximalDistanceForWeldline} mm`;
			} else {
				text = t("weld.weldMode.maxDistanceFallback");
			}
			let errorDialogMessage = t("weld.weldMode.someWeldsFailedMessage", { distance: text });
			if (errorMessages?.length) {
				errorDialogMessage += " " + t("weld.weldMode.errorListIntro");
			}
			store.errorDialog = {
				title: t("weld.weldMode.someWeldsFailedTitle"),
				messages: [errorDialogMessage, ...errorMessages],
			};
		}
	} catch (error: any) {
		store.errorDialog = {
			title: t("weld.weldMode.weldDefinitionFailedTitle"),
			messages: [error.message],
		};
	} finally {
		store.setLoadingState(LoadingState.Done);
	}
};

const setWeldingPairHoverState = (weldingPair: IFace[], hovered: boolean) => {
	// Only color them if the pair is already completed
	if (weldingPair.length !== 2) return;
	const color = hovered ? new THREE.Color(0xee00ee) : null;
	store.cam.setFaceColorById(weldingPair[0].groupId, color);
	store.cam.setFaceColorById(weldingPair[1].groupId, color);
};
</script>

<style scoped>
.delete-button {
	background-color: transparent;
	box-shadow: none;
	border: none;
	padding: 0px;
}
</style>
